"use client";

import { ModalsProvider } from "@mantine/modals";
import { AppProvider, ShellLayout, useAppContext } from "@repo/dashboard-core";
import { useParams } from "next/navigation";
import { useMemo, useState } from "react";

import { AuthHydrator } from "@repo/dashboard-core/auth";
import { useClientValue } from "@repo/dashboard-core/hooks";
import {
  openSubscriptionModal,
  SubscriptionModal,
} from "@/components/subscription/SubscriptionModal";
import {
  CRISP_DISMISS_STORAGE_KEY,
  CrispFloatingCTA,
} from "@/components/support/CrispFloatingCTA";
import { WhatsAppFloatingCTA } from "@/components/support/WhatsAppFloatingCTA";
import { TourLauncher } from "@/components/tour/TourLauncher";
import type { TuBrandConfig } from "@/config/brand";
import { esimNavItems } from "@/config/navigation";
import { esimRouteMap, routeMap } from "@/config/routes";
import { esimQuickActions, quickActions, shellConfig } from "@/config/shell";
import { BrandProvider, useBrand } from "@/lib/context/BrandContext";
import {
  PurchasesProvider,
  usePurchases,
} from "@/lib/context/PurchasesContext";
import { ServiceReviewProvider } from "@/lib/context/ServiceReviewContext";

function InnerShell({ children }: { children: React.ReactNode }) {
  const params = useParams<{ locale: string }>();
  const { subscriptionsInfo, userInfo } = useAppContext();
  const brand = useBrand();
  const crispEnabled = Boolean(brand.crispWebsiteId);
  const crispDismissedEarlier = useClientValue(
    () => sessionStorage.getItem(CRISP_DISMISS_STORAGE_KEY) === "1",
    false,
  );
  const [crispDismissedNow, setCrispDismissed] = useState(false);
  const crispDismissed = crispDismissedEarlier || crispDismissedNow;
  const { esimHome } = usePurchases();

  const resolvedConfig = useMemo(() => {
    const base = {
      ...shellConfig,
      navItems: esimHome ? esimNavItems : shellConfig.navItems,
      brand,
      headerExtra: <TourLauncher />,
    };

    const subscriptions = subscriptionsInfo?.runningSubscriptions;
    if (!subscriptions?.length) return base;

    return {
      ...base,
      onManageSubscription: () =>
        openSubscriptionModal({
          subscriptions,
          customerEmail: userInfo.email,
        }),
    };
  }, [
    brand,
    esimHome,
    subscriptionsInfo?.runningSubscriptions,
    userInfo.email,
  ]);

  return (
    <>
      <ShellLayout
        locale={params.locale}
        config={resolvedConfig}
        routeMap={esimHome ? esimRouteMap : routeMap}
        quickActions={esimHome ? esimQuickActions : quickActions}
      >
        {children}
      </ShellLayout>
      <WhatsAppFloatingCTA raised={crispEnabled && !crispDismissed} />
      {crispEnabled && (
        <CrispFloatingCTA
          websiteId={brand.crispWebsiteId}
          locale={params.locale}
          onDismiss={() => setCrispDismissed(true)}
        />
      )}
    </>
  );
}

export function DashboardShell({
  brand,
  children,
}: {
  brand: TuBrandConfig;
  children: React.ReactNode;
}) {
  return (
    <AppProvider>
      <AuthHydrator>
        <BrandProvider brand={brand}>
          <ModalsProvider modals={{ subscription: SubscriptionModal }}>
            <PurchasesProvider>
              <ServiceReviewProvider>
                <InnerShell>{children}</InnerShell>
              </ServiceReviewProvider>
            </PurchasesProvider>
          </ModalsProvider>
        </BrandProvider>
      </AuthHydrator>
    </AppProvider>
  );
}
